import React, {useState} from 'react';
import {IconButton} from '@mui/material';

import {Calendar} from '@icons/index';

import dayjs from '@utils/dayjs.utils';
import {DateRangePicker} from '@mui/x-date-pickers-pro/DateRangePicker';
import {DateRange} from '@mui/x-date-pickers-pro';
import {useTranslation} from 'react-i18next';


interface DateRangePickerComponentProps {
  color?: 'primary' | 'secondary';
  disabled?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  format?: string;
  handleChangeStartDate?: (date: Date | undefined | null) => void;
  handleChangeEndDate?: (date: Date | undefined | null) => void;
  label?: string;
  labelWidth?: number;
  inputWidth?: number;
  maxDate?: Date;
  minDate?: Date;
  placeholderStart?: string;
  placeholderEnd?: string;
  readOnly?: boolean;
  required?: boolean;
  size?: 'small';
  error?: string;
  startDate?: Date | null;
  endDate?: Date | null;
}

const DateRangePickerComponent = (props: DateRangePickerComponentProps) => {
  const {
    color = 'primary',
    disabled,
    disableFuture,
    disablePast,
    handleChangeStartDate,
    handleChangeEndDate,
    inputWidth,
    maxDate,
    minDate,
    placeholderStart,
    placeholderEnd,
    readOnly,
    size,
    startDate,
    endDate
  } = props;

  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <DateRangePicker
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      shouldDisableDate={(day) => {
        if (minDate) {
          return dayjs(day).isSameOrBefore(minDate, 'd');
        }
        if (maxDate) {
          return dayjs(day).isSameOrAfter(maxDate, 'd');
        }
        return false;
      }}
      // disableOpenPicker
      disabled={disabled}
      readOnly={readOnly}
      disablePast={disablePast}
      disableFuture={disableFuture}
      value={[startDate ? dayjs(startDate) : null, endDate ? dayjs(endDate) : null]}
      format={'DD.MM.YY'}
      localeText={{
        start: placeholderStart,
        end: placeholderEnd,
      }}
      onChange={(range: DateRange<dayjs.Dayjs>) => {
        if (!dayjs(range[0]).isSame(startDate) && dayjs(range[0]).isValid()) {
          handleChangeStartDate?.(dayjs(range[0]).startOf('day').toDate());

        }
        if (!dayjs(range[1]).isSame(endDate) && dayjs(range[1]).isValid()) {
          handleChangeEndDate?.(dayjs(range[1]).endOf('day').toDate());
        }

        if (range[0] == null && range[1] == null) {
          handleChangeStartDate?.(null);
          handleChangeEndDate?.(null);
        }
      }}
      // slots={{
      //   field: SingleInputDateRangeField
      // }}
      slotProps={{
        shortcuts: {
          items: [
            {
              label: t('dateRange.shortcuts.thisMonth'),
              getValue: () => {
                const today = dayjs();
                return [today.startOf('month'), today.endOf('month')];
              }
            },
            {
              label: t('dateRange.shortcuts.lastMonth'),
              getValue: () => {
                const today = dayjs();
                return [today.subtract(1, 'month').startOf('month'), today.subtract(1, 'month').endOf('month')];
              }
            },
            {
              label: t('dateRange.shortcuts.startOfYear'),
              getValue: () => {
                const today = dayjs();
                return [today.startOf('year'), today.endOf('date')];
              }
            },
            {
              label: t('dateRange.shortcuts.startOfYearToEndOfLastMonth'),
              getValue: () => {
                const today = dayjs();
                return [today.startOf('year'), today.subtract(1, 'month').endOf('month')];
              }
            },
            {
              label: t('dateRange.shortcuts.reset'),
              getValue: () => {
                return [null, null];
              }
            },
          ]
        },
        fieldSeparator: {
          children: ' - '
        },
        popper: {
          style: {
            zIndex: 1301
          }
        },
        textField: {
          label: undefined,
          sx: {flex: 1, width: inputWidth ? `${inputWidth}px` : undefined},
          color: color,
          size: size,
          disabled: disabled,
          focused: readOnly ? false : undefined,
          InputProps: {
            startAdornment: (
              <IconButton tabIndex={-1} onClick={() => {
                setOpen(true);
              }} sx={{mx: '-8px !important'}}>
                <Calendar/>
              </IconButton>
            ),
            endAdornment: null,
          },
        }
      }}
    />
  );
};

export default DateRangePickerComponent;
